import React, { useState, useEffect } from 'react';
import { sameIdentity } from '../../services/identity';
import { useAuth } from '../../contexts/AuthContext';
import { ExpenseService } from '../../services/expense';
import { ExpenseServiceWrapper } from '../../services/expenseWrapper';
import { AdminService } from '../../services/admin';
import { CognitoAdminService } from '../../services/cognitoAdmin';
import { isDemoMode } from '../../services/mockData';
import { notifyExpenseFinanceApproved, notifyExpenseFinanceRejected, notifyExpenseReimbursed } from '../../services/inAppNotification';
import { emailExpenseFinanceApproved, emailExpenseFinanceRejected, emailExpenseReimbursed } from '../../services/emailNotification';
import { useNotifications } from '../../contexts/NotificationContext';
import { Expense, ExpenseStatus, Category } from '../../types/expense';
import { User, UserRole } from '../../types/user';
import { canApproveFinance } from '../../utils/rolePermissions';
import ExpenseList from '../expenses/ExpenseList';
import ExpenseDetail from '../expenses/ExpenseDetail';
import MonthlySalarySummary from '../reports/MonthlySalarySummary';
import InlineLoader from '../common/InlineLoader';
import './AdminDashboard.css';

interface MonthlyTrendData {
  month: string;
  monthKey: string; // YYYY-MM format for filtering
  amount: number;
  count: number;
  submitted: number;
  approved: number;
  reimbursed: number;
}

interface CompanyMetrics {
  totalExpenses: number;
  totalAmount: number;
  monthlyTrend: MonthlyTrendData[];
  departmentBreakdown: Array<{
    department: string;
    amount: number;
    count: number;
  }>;
  categoryBreakdown: Array<{
    category: string;
    amount: number;
    count: number;
  }>;
  statusBreakdown: Array<{
    status: ExpenseStatus;
    count: number;
    amount: number;
  }>;
}

interface ExpenseWithUserInfo extends Expense {
  submitterName?: string;
  submitterEmail?: string;
  managerName?: string;
  managerEmail?: string;
}

interface CategoryFormData {
  name: string;
  description: string;
  isActive: boolean;
  maxAmount?: number;
  requiresReceipt?: boolean;
}

/**
 * Admin Dashboard Component
 * Requirements: 6.3, 4.4 - Admin dashboard with company-wide metrics and system configuration
 */
const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const { refresh } = useNotifications();
  const [allExpenses, setAllExpenses] = useState<Expense[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [metrics, setMetrics] = useState<CompanyMetrics>({
    totalExpenses: 0,
    totalAmount: 0,
    monthlyTrend: [],
    departmentBreakdown: [],
    categoryBreakdown: [],
    statusBreakdown: []
  });
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'expenses' | 'categories' | 'approvals' | 'salary-summary'>('overview');
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [pendingApprovals, setPendingApprovals] = useState<ExpenseWithUserInfo[]>([]);
  const [selectedMonthExpenses, setSelectedMonthExpenses] = useState<Expense[] | null>(null);
  const [selectedMonthLabel, setSelectedMonthLabel] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [adminSelectedIds, setAdminSelectedIds] = useState<Set<string>>(new Set());
  const [adminBulkLoading, setAdminBulkLoading] = useState(false);
  const [categoryForm, setCategoryForm] = useState<CategoryFormData>({
    name: '',
    description: '',
    isActive: true,
    maxAmount: undefined,
    requiresReceipt: false
  });
  
  // Filter states for All Expenses tab
  const [showExpenseFilters, setShowExpenseFilters] = useState(false);
  const [filteredExpenses, setFilteredExpenses] = useState<Expense[]>([]);
  const [expenseFilters, setExpenseFilters] = useState({
    manager: '', // email of selected manager
    employee: '', // email of selected employee
    status: '' as ExpenseStatus | '',
    datePreset: 'all' as 'all' | 'last-week' | 'last-month' | 'custom',
    dateFrom: '',
    dateTo: ''
  });
  const canPerformFinanceActions = Boolean(user?.role && canApproveFinance(user.role));

  useEffect(() => {
    if (user) {
      loadAdminData();
    }
  }, [user]);

  // Apply filters when allExpenses or filters change
  useEffect(() => {
    applyExpenseFilters();
  }, [allExpenses, expenseFilters, allUsers]);

  // Recalculate metrics when year changes
  useEffect(() => {
    if (allExpenses.length > 0) {
      calculateMetrics(allExpenses);
    }
  }, [selectedYear]);

  // Apply filters to expenses
  const applyExpenseFilters = () => {
    let filtered = [...allExpenses];
    
    // Filter by manager (show expenses from employees who report to this manager)
    if (expenseFilters.manager) {
      /*
       * sameIdentity throughout, not lowercased string equality. Each person owns two accounts
       * on two domains, so matching a report to its manager and then an expense to that report
       * both dropped whichever half was filed under the other address — the manager filter
       * silently returned a subset of the team's expenses.
       */
      const teamEmails = allUsers
        .filter(u => sameIdentity(u.managerEmail, expenseFilters.manager))
        .map(u => u.email);

      filtered = filtered.filter(expense => {
        const submitterEmail = getUserInfo(expense.submitterId).email;
        return teamEmails.some(e => sameIdentity(e, submitterEmail));
      });
    }
    
    // Filter by specific employee
    if (expenseFilters.employee) {
      filtered = filtered.filter(expense =>
        sameIdentity(getUserInfo(expense.submitterId).email, expenseFilters.employee)
      );
    }
    
    // Filter by status
    if (expenseFilters.status) {
      filtered = filtered.filter(expense => expense.status === expenseFilters.status);
    }
    
    // Filter by date
    if (expenseFilters.dateFrom) {
      filtered = filtered.filter(expense => expense.date >= expenseFilters.dateFrom);
    }
    if (expenseFilters.dateTo) {
      filtered = filtered.filter(expense => expense.date <= expenseFilters.dateTo);
    }
    
    setFilteredExpenses(filtered);
  };

  // Handle date preset changes for expense filters
  const handleExpenseDatePresetChange = (preset: 'all' | 'last-week' | 'last-month' | 'custom') => {
    const now = new Date();
    let dateFrom = '';
    let dateTo = '';
    
    if (preset === 'last-week') {
      const lastWeek = new Date(now);
      lastWeek.setDate(now.getDate() - 7);
      dateFrom = lastWeek.toISOString().split('T')[0];
      dateTo = now.toISOString().split('T')[0];
    } else if (preset === 'last-month') {
      const lastMonth = new Date(now);
      lastMonth.setMonth(now.getMonth() - 1);
      dateFrom = lastMonth.toISOString().split('T')[0];
      dateTo = now.toISOString().split('T')[0];
    }
    
    setExpenseFilters(prev => ({
      ...prev,
      datePreset: preset,
      dateFrom: preset === 'custom' ? prev.dateFrom : dateFrom,
      dateTo: preset === 'custom' ? prev.dateTo : dateTo
    }));
  };

  // Clear expense filters
  const clearExpenseFilters = () => {
    setExpenseFilters({
      manager: '',
      employee: '',
      status: '',
      datePreset: 'all',
      dateFrom: '',
      dateTo: ''
    });
  };

  // Get list of managers for filter dropdown
  const getManagers = (): User[] => {
    return allUsers.filter(u => 
      u.role === UserRole.MANAGER || 
      u.role === UserRole.FINANCE_ADMIN || 
      u.role === UserRole.SUPER_ADMIN
    );
  };

  // Get list of employees for filter dropdown
  const getEmployees = (): User[] => {
    // If a manager is selected, only show their direct reports
    if (expenseFilters.manager) {
      return allUsers.filter(u => sameIdentity(u.managerEmail, expenseFilters.manager));
    }
    return allUsers;
  };

  // Helper to get user info by submitterId
  const getUserInfo = (submitterId: string | undefined): { name: string; email: string; managerName?: string; managerEmail?: string } => {
    if (!submitterId) return { name: 'Unknown', email: '' };
    
    // Try to find user by ID or email
    // sameIdentity: an expense stored against either of the submitter's two domain addresses
    // must still resolve to them, or the row shows no submitter name.
    const foundUser = allUsers.find(u =>
      u.id === submitterId ||
      sameIdentity(u.email, submitterId)
    );
    
    if (foundUser) {
      // Find manager info
      let managerName: string | undefined;
      let managerEmail = foundUser.managerEmail;
      if (managerEmail) {
        const manager = allUsers.find(u => (u.email || '').toLowerCase() === managerEmail?.toLowerCase());
        managerName = manager?.name;
      }
      
      return {
        name: foundUser.name,
        email: foundUser.email,
        managerName,
        managerEmail
      };
    }
    
    // Fallback: extract name from email
    if (submitterId.includes('@')) {
      const namePart = submitterId.split('@')[0];
      return {
        name: namePart.split(/[._-]/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
        email: submitterId
      };
    }
    
    return { name: 'Unknown', email: submitterId };
  };

  const loadAdminData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load users first for mapping submitter info
      let users: User[] = [];
      try {
        const adminEndpoint = process.env.REACT_APP_ADMIN_API_ENDPOINT;
        if (adminEndpoint) {
          const response = await CognitoAdminService.listUsers();
          if (response.users) {
            users = response.users.map((u: any) => ({
              id: u.username || u.email || '',
              email: u.email || '',
              name: u.name || 'Unknown',
              employeeId: u.employeeId || '',
              department: u.department || '',
              managerEmail: u.managerEmail || '',
              role: CognitoAdminService.mapCognitoRole(u.role),
              createdAt: u.createdAt || new Date().toISOString(),
              updatedAt: u.updatedAt || new Date().toISOString()
            }));
          }
        }
      } catch (userError) {
        console.log('[Admin Dashboard] Could not load users:', userError);
      }
      setAllUsers(users);
      
      console.log('[Admin Dashboard][Load] request start', {
        userRole: user?.role,
        expenseSource: 'ExpenseServiceWrapper.getAllExpenses',
        appliedFilters: {},
      });

      const [expensesResult, loadedCategories] = await Promise.all([
        ExpenseServiceWrapper.getAllExpenses(user?.role),
        AdminService.getCategories().catch(categoryError => {
          console.warn('[Admin Dashboard] Could not load configured categories:', categoryError);
          return [] as Category[];
        }),
      ]);
      
      // Filter pending approvals and enrich with user info
      // Use local `users` array directly (not stale state)
      const allPending = (expensesResult.items || []).filter(
        e => e.status === ExpenseStatus.MANAGER_APPROVED || e.status === ExpenseStatus.FINANCE_APPROVED
      );
      
      // Inline user lookup using the freshly loaded users array
      const lookupUser = (submitterId: string | undefined) => {
        if (!submitterId) return { name: 'Unknown', email: '', managerName: undefined, managerEmail: undefined };
        const found = users.find(u => 
          u.id === submitterId || u.email === submitterId || 
          ((u.email || '').toLowerCase() === (submitterId || '').toLowerCase())
        );
        if (found) {
          let managerName: string | undefined;
          if (found.managerEmail) {
            const mgr = users.find(u => (u.email || '').toLowerCase() === (found.managerEmail || '').toLowerCase());
            managerName = mgr?.name;
          }
          return { name: found.name, email: found.email, managerName, managerEmail: found.managerEmail };
        }
        if (submitterId.includes('@')) {
          const namePart = submitterId.split('@')[0];
          return { name: namePart.split(/[._-]/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '), email: submitterId, managerName: undefined, managerEmail: undefined };
        }
        return { name: 'Unknown', email: submitterId, managerName: undefined, managerEmail: undefined };
      };
      
      const enrichedPending: ExpenseWithUserInfo[] = allPending.map(expense => {
        const userInfo = lookupUser(expense.submitterId);
        return {
          ...expense,
          submitterName: userInfo.name,
          submitterEmail: userInfo.email,
          managerName: userInfo.managerName,
          managerEmail: userInfo.managerEmail
        };
      });
      
      setAllExpenses(expensesResult.items || []);
      setPendingApprovals(enrichedPending);
      setCategories(loadedCategories);
      calculateMetrics(expensesResult.items || [], users);
      
      console.log('[Admin Dashboard][Load] request complete', {
        userRole: user?.role,
        recordsScanned: expensesResult.total ?? expensesResult.items?.length ?? 0,
        recordsReturned: expensesResult.items?.length ?? 0,
        pendingApprovals: allPending.length,
        configuredCategories: loadedCategories.length,
        appliedFilters: {},
      });
    } catch (err) {
      console.error('[Admin Dashboard] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };
  const calculateMetrics = (expenses: Expense[], users: User[] = allUsers) => {
    const metrics: CompanyMetrics = {
      totalExpenses: expenses.length,
      totalAmount: expenses.reduce((sum, expense) => sum + Number(expense.amount || 0), 0),
      monthlyTrend: [],
      departmentBreakdown: [],
      categoryBreakdown: [],
      statusBreakdown: []
    };

    // Calculate monthly trend - always show all 12 months for any year
    const monthlyData = new Map<string, { amount: number; count: number; submitted: number; approved: number; reimbursed: number }>();
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-indexed (0 = January, 11 = December)
    
    // Always generate all 12 months (Jan to Dec) for any selected year
    for (let month = 0; month <= 11; month++) {
      const date = new Date(selectedYear, month, 1);
      const monthKey = date.toISOString().substring(0, 7); // YYYY-MM
      monthlyData.set(monthKey, { amount: 0, count: 0, submitted: 0, approved: 0, reimbursed: 0 });
    }

    // Only populate data for months that have passed or are current
    expenses.forEach(expense => {
      const expenseDate = new Date(expense.date);
      const expenseYear = expenseDate.getFullYear();
      const expenseMonth = expenseDate.getMonth();
      const monthKey = expenseDate.toISOString().substring(0, 7);
      
      // Only include expense if:
      // 1. It's in the selected year
      // 2. For current year, only include if month has passed or is current month
      // 3. For past years, include all expenses
      const isValidExpense = expenseYear === selectedYear && 
        (selectedYear < currentYear || (selectedYear === currentYear && expenseMonth <= currentMonth));
      
      if (isValidExpense && monthlyData.has(monthKey)) {
        const current = monthlyData.get(monthKey)!;
        const isSubmitted = [ExpenseStatus.SUBMITTED, ExpenseStatus.MANAGER_APPROVED].includes(expense.status);
        const isApproved = [ExpenseStatus.FINANCE_APPROVED].includes(expense.status);
        const isReimbursed = [ExpenseStatus.REIMBURSED].includes(expense.status);
        
        monthlyData.set(monthKey, {
          amount: current.amount + Number(expense.amount || 0),
          count: current.count + 1,
          submitted: current.submitted + (isSubmitted ? 1 : 0),
          approved: current.approved + (isApproved ? 1 : 0),
          reimbursed: current.reimbursed + (isReimbursed ? 1 : 0)
        });
      }
    });

    metrics.monthlyTrend = Array.from(monthlyData.entries()).map(([monthKey, data]) => ({
      month: new Date(monthKey + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      monthKey: monthKey,
      amount: data.amount,
      count: data.count,
      submitted: data.submitted,
      approved: data.approved,
      reimbursed: data.reimbursed
    }));

    // Calculate department breakdown
    const departmentData = new Map<string, { amount: number; count: number }>();
    expenses.forEach(expense => {
      const submitter = users.find(candidate =>
        candidate.id === expense.submitterId ||
        sameIdentity(candidate.email, expense.submitterId)
      );
      const storedDepartment = expense.department?.split('|')[0]?.trim();
      const department = storedDepartment || submitter?.department?.trim() || 'Unassigned';
      const current = departmentData.get(department) || { amount: 0, count: 0 };
      departmentData.set(department, {
        amount: current.amount + Number(expense.amount || 0),
        count: current.count + 1
      });
    });

    metrics.departmentBreakdown = Array.from(departmentData.entries()).map(([department, data]) => ({
      department,
      amount: data.amount,
      count: data.count
    }));

    // Calculate category breakdown
    const categoryData = new Map<string, { amount: number; count: number }>();
    expenses.forEach(expense => {
      const current = categoryData.get(expense.category) || { amount: 0, count: 0 };
      categoryData.set(expense.category, {
        amount: current.amount + Number(expense.amount || 0),
        count: current.count + 1
      });
    });

    metrics.categoryBreakdown = Array.from(categoryData.entries()).map(([category, data]) => ({
      category,
      amount: data.amount,
      count: data.count
    }));

    // Calculate status breakdown
    const statusData = new Map<ExpenseStatus, { amount: number; count: number }>();
    Object.values(ExpenseStatus).forEach(status => {
      statusData.set(status, { amount: 0, count: 0 });
    });

    expenses.forEach(expense => {
      const current = statusData.get(expense.status)!;
      statusData.set(expense.status, {
        amount: current.amount + Number(expense.amount || 0),
        count: current.count + 1
      });
    });

    metrics.statusBreakdown = Array.from(statusData.entries()).map(([status, data]) => ({
      status,
      amount: data.amount,
      count: data.count
    }));

    console.log('[Admin Dashboard][Aggregation]', {
      userRole: user?.role,
      recordsReturned: expenses.length,
      totalExpenses: metrics.totalExpenses,
      totalAmount: metrics.totalAmount,
      categories: metrics.categoryBreakdown.length,
      departments: metrics.departmentBreakdown.length,
      appliedFilters: {},
    });
    setMetrics(metrics);
  };

  const handleCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingCategory) {
        await AdminService.updateCategory(editingCategory.id, categoryForm);
      } else {
        await AdminService.createCategory(categoryForm);
      }
      
      await loadAdminData(); // Reload data
      resetCategoryForm();
    } catch (error) {
      console.error('Failed to save category:', error);
      alert('Failed to save category. Please try again.');
    }
  };

  const handleEditCategory = (category: Category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      description: category.description || '',
      isActive: category.isActive,
      maxAmount: category.limits?.maxAmount,
      requiresReceipt: category.limits?.requiresReceipt || false
    });
    setShowCategoryForm(true);
  };

  const handleDeleteCategory = async (category: Category) => {
    if (window.confirm(`Are you sure you want to delete the category "${category.name}"?`)) {
      try {
        await AdminService.deleteCategory(category.id);
        await loadAdminData();
      } catch (error) {
        console.error('Failed to delete category:', error);
        alert('Failed to delete category. Please try again.');
      }
    }
  };

  const resetCategoryForm = () => {
    setShowCategoryForm(false);
    setEditingCategory(null);
    setCategoryForm({
      name: '',
      description: '',
      isActive: true,
      maxAmount: undefined,
      requiresReceipt: false
    });
  };

  const handleFinalApproval = async (expense: Expense, comments?: string) => {
    try {
      if (isDemoMode() && user) {
        await ExpenseServiceWrapper.approveExpense(expense.id, user.id, comments);
      } else {
        await AdminService.finalApproveExpense(expense.id, comments);
      }
      if (user) {
        // Get submitter email - use enriched data if available, otherwise fall back to submitterId
        const submitterEmail = (expense as ExpenseWithUserInfo).submitterEmail || expense.submitterId;
        notifyExpenseFinanceApproved(submitterEmail, user.name, expense.title, expense.amount, expense.id);
        emailExpenseFinanceApproved(submitterEmail, user.name, expense.title, expense.amount, expense.id);
      }
      await loadAdminData();
      refresh();
    } catch (error) {
      console.error('Failed to perform final approval:', error);
      alert('Failed to perform final approval. Please try again.');
    }
  };

  const handleMarkReimbursed = async (expense: Expense, details?: {
    method?: string;
    transactionId?: string;
    notes?: string;
  }) => {
    try {
      if (isDemoMode()) {
        await ExpenseServiceWrapper.markReimbursed(expense.id);
      } else {
        await AdminService.markExpenseReimbursed(expense.id, details);
      }
      // Get submitter email - use enriched data if available, otherwise fall back to submitterId
      const submitterEmail = (expense as ExpenseWithUserInfo).submitterEmail || expense.submitterId;
      notifyExpenseReimbursed(submitterEmail, expense.title, expense.amount, expense.id);
      emailExpenseReimbursed(submitterEmail, expense.title, expense.amount, expense.id);
      await loadAdminData();
      refresh();
    } catch (error) {
      console.error('Failed to mark expense as reimbursed:', error);
      alert('Failed to mark expense as reimbursed. Please try again.');
    }
  };

  const handleRejectExpense = async (expense: Expense, comments: string) => {
    try {
      if (user) {
        await ExpenseServiceWrapper.rejectExpense(expense.id, user.id, comments);
        // Get submitter email - use enriched data if available, otherwise fall back to submitterId
        const submitterEmail = (expense as ExpenseWithUserInfo).submitterEmail || expense.submitterId;
        notifyExpenseFinanceRejected(submitterEmail, user.name, expense.title, expense.id, comments);
        emailExpenseFinanceRejected(submitterEmail, user.name, expense.title, expense.id, comments);
      }
      await loadAdminData();
      refresh();
    } catch (error) {
      console.error('Failed to reject expense:', error);
      alert('Failed to reject expense. Please try again.');
    }
  };

  // Admin selection helpers
  const toggleAdminSelect = (id: string) => {
    setAdminSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAdminSelectAll = () => {
    const managerApproved = pendingApprovals.filter(e => e.status === ExpenseStatus.MANAGER_APPROVED);
    if (adminSelectedIds.size === managerApproved.length) {
      setAdminSelectedIds(new Set());
    } else {
      setAdminSelectedIds(new Set(managerApproved.map(e => e.id)));
    }
  };

  const handleAdminBulkApprove = async (ids: string[]) => {
    if (ids.length === 0) return;
    if (!window.confirm(`Approve ${ids.length} expense(s)?`)) return;
    setAdminBulkLoading(true);
    for (const id of ids) {
      const expense = pendingApprovals.find(e => e.id === id);
      if (expense && expense.status === ExpenseStatus.MANAGER_APPROVED) {
        try { await handleFinalApproval(expense, 'Bulk approved'); } catch { /* continue */ }
      }
    }
    setAdminBulkLoading(false);
    setAdminSelectedIds(new Set());
  };

  const formatAmount = (amount: number): string => {
    return ExpenseService.formatAmount(amount);
  };

  const getStatusColor = (status: ExpenseStatus): string => {
    return ExpenseService.getStatusColor(status);
  };

  // Handle monthly trend bar click
  const handleMonthClick = (monthData: MonthlyTrendData) => {
    if (monthData.count === 0) return;
    
    // Filter expenses for the selected month
    const monthExpenses = allExpenses.filter(expense => {
      const expenseDate = new Date(expense.date);
      const expenseMonthKey = expenseDate.toISOString().substring(0, 7);
      return expenseMonthKey === monthData.monthKey;
    });
    
    setSelectedMonthExpenses(monthExpenses);
    setSelectedMonthLabel(monthData.month);
  };

  // Close month expenses modal
  const closeMonthExpensesModal = () => {
    setSelectedMonthExpenses(null);
    setSelectedMonthLabel('');
  };;

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="loading"><InlineLoader message="Loading admin dashboard" /></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard">
        <div className="error">
          <h3>Error Loading Dashboard</h3>
          <p>{error}</p>
          <button onClick={loadAdminData} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h1>Admin Dashboard</h1>
        <p>Company-wide expense management for {user?.name}</p>
      </div>

      {/* Navigation Tabs */}
      <div className="dashboard-tabs">
        <button 
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab-button ${activeTab === 'expenses' ? 'active' : ''}`}
          onClick={() => setActiveTab('expenses')}
        >
          All Expenses
        </button>
        <button 
          className={`tab-button ${activeTab === 'approvals' ? 'active' : ''}`}
          onClick={() => setActiveTab('approvals')}
        >
          Pending Approvals
          {pendingApprovals.length > 0 && (
            <span className="tab-badge">{pendingApprovals.length}</span>
          )}
        </button>
        <button 
          className={`tab-button ${activeTab === 'salary-summary' ? 'active' : ''}`}
          onClick={() => setActiveTab('salary-summary')}
        >
          Salary Summary
        </button>
        <button 
          className={`tab-button ${activeTab === 'categories' ? 'active' : ''}`}
          onClick={() => setActiveTab('categories')}
        >
          Categories
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="overview-tab">
          {/* Summary Cards */}
          <div className="summary-cards">
            <div className="summary-card">
              <div className="card-icon">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
                </svg>
              </div>
              <h3>Total Expenses</h3>
              <div className="count">{metrics.totalExpenses}</div>
              <div className="description">All time</div>
            </div>
            
            <div className="summary-card">
              <div className="card-icon">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.31-8.86c-1.77-.45-2.34-.94-2.34-1.67 0-.84.79-1.43 2.1-1.43 1.38 0 1.9.66 1.94 1.64h1.71c-.05-1.34-.87-2.57-2.49-2.97V5H10.9v1.69c-1.51.32-2.72 1.3-2.72 2.81 0 1.79 1.49 2.69 3.66 3.21 1.95.46 2.34 1.15 2.34 1.87 0 .53-.39 1.39-2.1 1.39-1.6 0-2.23-.72-2.32-1.64H8.04c.1 1.7 1.36 2.66 2.86 2.97V19h2.34v-1.67c1.52-.29 2.72-1.16 2.73-2.77-.01-2.2-1.9-2.96-3.66-3.42z"/>
                </svg>
              </div>
              <h3>Total Amount</h3>
              <div className="amount">{formatAmount(metrics.totalAmount)}</div>
              <div className="description">All expenses</div>
            </div>
            
            <div className="summary-card">
              <div className="card-icon">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              </div>
              <h3>Categories</h3>
              <div className="count">{metrics.categoryBreakdown.length}</div>
              <div className="description">With expenses</div>
            </div>
            
            <div className="summary-card">
              <div className="card-icon">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/>
                </svg>
              </div>
              <h3>Departments</h3>
              <div className="count">{metrics.departmentBreakdown.length}</div>
              <div className="description">With expenses</div>
            </div>
          </div>

          {/* Monthly Trend Chart */}
          <div className="chart-section">
            <div className="chart-header">
              <h2>Monthly Trend</h2>
              <div className="year-selector">
                <button 
                  className="year-btn"
                  onClick={() => setSelectedYear(selectedYear - 1)}
                  disabled={selectedYear <= 2020}
                >
                  ◀
                </button>
                <span className="year-display">{selectedYear}</span>
                <button 
                  className="year-btn"
                  onClick={() => setSelectedYear(selectedYear + 1)}
                  disabled={selectedYear >= new Date().getFullYear()}
                >
                  ▶
                </button>
              </div>
            </div>
            <p className="chart-hint">Click on a bar to view expenses for that month</p>
            <div className="trend-chart">
              {metrics.monthlyTrend.map((month, index) => (
                <div 
                  key={index} 
                  className={`trend-bar ${month.count > 0 ? 'clickable' : ''}`}
                  onClick={() => handleMonthClick(month)}
                  role={month.count > 0 ? 'button' : undefined}
                  tabIndex={month.count > 0 ? 0 : undefined}
                >
                  <div 
                    className="bar" 
                    style={{ 
                      height: `${Math.max(10, (month.amount / Math.max(...metrics.monthlyTrend.map(m => m.amount), 1)) * 100)}px` 
                    }}
                  >
                    <div className="bar-tooltip">
                      <div className="tooltip-header">{month.month}</div>
                      <div className="tooltip-row">
                        <span>Total Amount:</span>
                        <strong>{formatAmount(month.amount)}</strong>
                      </div>
                      <div className="tooltip-row">
                        <span>Total Expenses:</span>
                        <strong>{month.count}</strong>
                      </div>
                      <div className="tooltip-divider"></div>
                      <div className="tooltip-row status-submitted">
                        <span>📤 Submitted:</span>
                        <strong>{month.submitted}</strong>
                      </div>
                      <div className="tooltip-row status-approved">
                        <span>✅ Approved:</span>
                        <strong>{month.approved}</strong>
                      </div>
                      <div className="tooltip-row status-reimbursed">
                        <span>💰 Reimbursed:</span>
                        <strong>{month.reimbursed}</strong>
                      </div>
                    </div>
                  </div>
                  <div className="bar-label">{month.month.split(' ')[0]}</div>
                  <div className="bar-count">{month.count}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Breakdown Sections */}
          <div className="breakdown-sections">
            {/* Category Breakdown */}
            <div className="breakdown-section">
              <h3>Category Breakdown</h3>
              <div className="breakdown-list">
                {metrics.categoryBreakdown.map(item => (
                  <div key={item.category} className="breakdown-item">
                    <div className="breakdown-info">
                      <span className="breakdown-name">{item.category}</span>
                      <span className="breakdown-count">{item.count} expenses</span>
                    </div>
                    <div className="breakdown-amount">{formatAmount(item.amount)}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Status Breakdown */}
            <div className="breakdown-section">
              <h3>Status Breakdown</h3>
              <div className="breakdown-list">
                {metrics.statusBreakdown.filter(item => item.count > 0).map(item => (
                  <div key={item.status} className="breakdown-item">
                    <div className="breakdown-info">
                      <span className={`breakdown-name status-${getStatusColor(item.status)}`}>
                        {item.status}
                      </span>
                      <span className="breakdown-count">{item.count} expenses</span>
                    </div>
                    <div className="breakdown-amount">{formatAmount(item.amount)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Expenses Tab */}
      {activeTab === 'expenses' && (
        <div className="expenses-tab">
          <div className="expenses-header">
            <h2>All Expenses ({filteredExpenses.length})</h2>
            <button 
              className="toggle-filters-button"
              onClick={() => setShowExpenseFilters(!showExpenseFilters)}
            >
              {showExpenseFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
          </div>
          
          {/* Expense Filters */}
          {showExpenseFilters && (
            <div className="expense-filters">
              {/* Quick Date Filters */}
              <div className="filter-row quick-filters">
                <label>Date:</label>
                <div className="quick-filter-buttons">
                  <button 
                    className={`quick-filter-btn ${expenseFilters.datePreset === 'all' ? 'active' : ''}`}
                    onClick={() => handleExpenseDatePresetChange('all')}
                  >
                    All Time
                  </button>
                  <button 
                    className={`quick-filter-btn ${expenseFilters.datePreset === 'last-week' ? 'active' : ''}`}
                    onClick={() => handleExpenseDatePresetChange('last-week')}
                  >
                    Last Week
                  </button>
                  <button 
                    className={`quick-filter-btn ${expenseFilters.datePreset === 'last-month' ? 'active' : ''}`}
                    onClick={() => handleExpenseDatePresetChange('last-month')}
                  >
                    Last Month
                  </button>
                  <button 
                    className={`quick-filter-btn ${expenseFilters.datePreset === 'custom' ? 'active' : ''}`}
                    onClick={() => handleExpenseDatePresetChange('custom')}
                  >
                    Custom
                  </button>
                </div>
              </div>
              
              <div className="filter-row">
                {/* Manager Filter */}
                <div className="filter-group">
                  <label>Manager:</label>
                  <select 
                    value={expenseFilters.manager}
                    onChange={(e) => setExpenseFilters(prev => ({ ...prev, manager: e.target.value, employee: '' }))}
                  >
                    <option value="">All Managers</option>
                    {getManagers().map(manager => (
                      <option key={manager.email} value={manager.email}>
                        {manager.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Employee Filter */}
                <div className="filter-group">
                  <label>Employee:</label>
                  <select 
                    value={expenseFilters.employee}
                    onChange={(e) => setExpenseFilters(prev => ({ ...prev, employee: e.target.value }))}
                  >
                    <option value="">All Employees</option>
                    {getEmployees().map(emp => (
                      <option key={emp.email} value={emp.email}>
                        {emp.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Status Filter */}
                <div className="filter-group">
                  <label>Status:</label>
                  <select 
                    value={expenseFilters.status}
                    onChange={(e) => setExpenseFilters(prev => ({ ...prev, status: e.target.value as ExpenseStatus | '' }))}
                  >
                    <option value="">All Statuses</option>
                    {Object.values(ExpenseStatus).map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
                
                {/* Custom Date Range */}
                {expenseFilters.datePreset === 'custom' && (
                  <>
                    <div className="filter-group">
                      <label>From:</label>
                      <input 
                        type="date"
                        value={expenseFilters.dateFrom}
                        onChange={(e) => setExpenseFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                      />
                    </div>
                    <div className="filter-group">
                      <label>To:</label>
                      <input 
                        type="date"
                        value={expenseFilters.dateTo}
                        onChange={(e) => setExpenseFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                      />
                    </div>
                  </>
                )}
                
                <button className="clear-filters-btn" onClick={clearExpenseFilters}>
                  Clear Filters
                </button>
              </div>
            </div>
          )}
          
          <div className="expenses-summary">
            <span>Showing: {filteredExpenses.length} expenses</span>
            <span>Amount: {formatAmount(filteredExpenses.reduce((sum, e) => sum + e.amount, 0))}</span>
          </div>
          
          <ExpenseList 
            expenses={filteredExpenses.map(expense => {
              const userInfo = getUserInfo(expense.submitterId);
              return {
                ...expense,
                submitterName: userInfo.name,
                submitterEmail: userInfo.email,
                managerName: userInfo.managerName,
                managerEmail: userInfo.managerEmail
              } as any;
            })}
            onExpenseSelect={setSelectedExpense}
            showActions={true}
          />
        </div>
      )}

      {/* Pending Approvals Tab */}
      {activeTab === 'approvals' && (
        <div className="approvals-tab">
          <div className="approvals-header">
            <h2>Pending Approvals</h2>
            <div className="approvals-summary">
              <span>{pendingApprovals.length} expenses awaiting approval</span>
              {canPerformFinanceActions && pendingApprovals.filter(e => e.status === ExpenseStatus.MANAGER_APPROVED).length > 0 && (
                <div className="bulk-actions" style={{ display: 'flex', gap: '8px', marginLeft: '12px' }}>
                  {adminSelectedIds.size > 0 && (
                    <button className="approval-button approve" onClick={() => handleAdminBulkApprove(Array.from(adminSelectedIds))} disabled={adminBulkLoading}>
                      {adminBulkLoading ? 'Approving...' : `Approve Selected (${adminSelectedIds.size})`}
                    </button>
                  )}
                  <button className="approval-button approve" onClick={() => handleAdminBulkApprove(pendingApprovals.filter(e => e.status === ExpenseStatus.MANAGER_APPROVED).map(e => e.id))} disabled={adminBulkLoading}>
                    {adminBulkLoading ? 'Approving...' : 'Approve All'}
                  </button>
                </div>
              )}
            </div>
          </div>
          
          {pendingApprovals.length === 0 ? (
            <div className="no-approvals">
              <p>No expenses pending approval</p>
            </div>
          ) : (
            <>
              {canPerformFinanceActions && pendingApprovals.filter(e => e.status === ExpenseStatus.MANAGER_APPROVED).length > 0 && (
                <div className="select-all-row" style={{ padding: '8px 16px', background: '#f8fafc', borderRadius: '8px', marginBottom: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 600, color: '#475569', cursor: 'pointer' }}>
                    <input type="checkbox" checked={adminSelectedIds.size === pendingApprovals.filter(e => e.status === ExpenseStatus.MANAGER_APPROVED).length && adminSelectedIds.size > 0} onChange={toggleAdminSelectAll} />
                    Select All Pending Finance Approval
                  </label>
                </div>
              )}
              <div className="approvals-list">
              {pendingApprovals.map(expense => (
                <div key={expense.id} className={`approval-item ${adminSelectedIds.has(expense.id) ? 'selected' : ''}`}>
                  {canPerformFinanceActions && expense.status === ExpenseStatus.MANAGER_APPROVED && (
                    <div style={{ padding: '0 12px 0 0' }}>
                      <input type="checkbox" checked={adminSelectedIds.has(expense.id)} onChange={() => toggleAdminSelect(expense.id)} />
                    </div>
                  )}
                  <div className="approval-info">
                    <h4>{expense.title}</h4>
                    <div className="approval-meta">
                      <span className="expense-amount">{formatAmount(expense.amount)}</span>
                      <span className="expense-category">{expense.category}</span>
                      <span className="expense-date">{new Date(expense.date).toLocaleDateString()}</span>
                      <span className={`expense-status ${getStatusColor(expense.status)}`}>
                        {expense.status}
                      </span>
                    </div>
                    <div className="submitter-info">
                      <span className="submitter">👤 Submitted by: <strong>{expense.submitterName || 'Unknown'}</strong></span>
                      {expense.managerName && (
                        <span className="manager">👔 Manager: <strong>{expense.managerName}</strong></span>
                      )}
                    </div>
                    {expense.description && (
                      <p className="expense-description">{expense.description}</p>
                    )}
                  </div>
                  
                  <div className="approval-actions">
                    <button 
                      className="approval-button view"
                      onClick={() => setSelectedExpense(expense)}
                    >
                      View Details
                    </button>
                    
                    {canPerformFinanceActions && expense.status === ExpenseStatus.MANAGER_APPROVED && (
                      <>
                        <button 
                          className="approval-button approve"
                          onClick={() => {
                            const comments = prompt('Enter approval comments (optional):');
                            if (comments !== null) {
                              handleFinalApproval(expense, comments || undefined);
                            }
                          }}
                        >
                          Final Approve
                        </button>
                        <button 
                          className="approval-button reject"
                          onClick={() => {
                            const comments = prompt('Enter rejection reason:');
                            if (comments) {
                              handleRejectExpense(expense, comments);
                            }
                          }}
                        >
                          Reject
                        </button>
                      </>
                    )}
                    
                    {canPerformFinanceActions && expense.status === ExpenseStatus.FINANCE_APPROVED && (
                      <button 
                        className="approval-button reimburse"
                        onClick={() => {
                          const method = prompt('Enter reimbursement method (optional):');
                          const transactionId = prompt('Enter transaction ID (optional):');
                          const notes = prompt('Enter notes (optional):');
                          
                          handleMarkReimbursed(expense, {
                            method: method || undefined,
                            transactionId: transactionId || undefined,
                            notes: notes || undefined
                          });
                        }}
                      >
                        Mark Reimbursed
                      </button>
                    )}
                  </div>
                </div>
              ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Categories Tab */}
      {activeTab === 'categories' && (
        <div className="categories-tab">
          <div className="categories-header">
            <h2>Expense Categories</h2>
            <button 
              className="add-category-button"
              onClick={() => setShowCategoryForm(true)}
            >
              Add Category
            </button>
          </div>

          <div className="categories-list">
            {categories.map(category => (
              <div key={category.id} className={`category-item ${!category.isActive ? 'inactive' : ''}`}>
                <div className="category-info">
                  <h4>{category.name}</h4>
                  {category.description && (
                    <p className="category-description">{category.description}</p>
                  )}
                  <div className="category-meta">
                    <span className={`category-status ${category.isActive ? 'active' : 'inactive'}`}>
                      {category.isActive ? 'Active' : 'Inactive'}
                    </span>
                    {category.limits?.maxAmount && (
                      <span className="category-limit">
                        Max: {formatAmount(category.limits.maxAmount)}
                      </span>
                    )}
                    {category.limits?.requiresReceipt && (
                      <span className="category-requirement">Receipt Required</span>
                    )}
                  </div>
                </div>
                
                <div className="category-actions">
                  <button 
                    className="category-action-button edit"
                    onClick={() => handleEditCategory(category)}
                  >
                    Edit
                  </button>
                  <button 
                    className="category-action-button delete"
                    onClick={() => handleDeleteCategory(category)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Category Form Modal */}
          {showCategoryForm && (
            <div className="category-form-modal">
              <div className="modal-backdrop" onClick={resetCategoryForm} />
              <div className="modal-content">
                <div className="modal-header">
                  <h3>{editingCategory ? 'Edit Category' : 'Add Category'}</h3>
                  <button className="close-button" onClick={resetCategoryForm}>×</button>
                </div>
                
                <form onSubmit={handleCategorySubmit} className="category-form">
                  <div className="form-group">
                    <label>Name:</label>
                    <input
                      type="text"
                      value={categoryForm.name}
                      onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Description:</label>
                    <textarea
                      value={categoryForm.description}
                      onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={categoryForm.isActive}
                        onChange={(e) => setCategoryForm(prev => ({ ...prev, isActive: e.target.checked }))}
                      />
                      Active
                    </label>
                  </div>
                  
                  <div className="form-group">
                    <label>Maximum Amount (optional):</label>
                    <input
                      type="number"
                      step="0.01"
                      value={categoryForm.maxAmount || ''}
                      onChange={(e) => setCategoryForm(prev => ({ 
                        ...prev, 
                        maxAmount: e.target.value ? parseFloat(e.target.value) : undefined 
                      }))}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={categoryForm.requiresReceipt}
                        onChange={(e) => setCategoryForm(prev => ({ ...prev, requiresReceipt: e.target.checked }))}
                      />
                      Require Receipt
                    </label>
                  </div>
                  
                  <div className="form-actions">
                    <button type="button" onClick={resetCategoryForm} className="cancel-button">
                      Cancel
                    </button>
                    <button type="submit" className="save-button">
                      {editingCategory ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Salary Summary Tab */}
      {activeTab === 'salary-summary' && (
        <div className="salary-summary-tab">
          <MonthlySalarySummary />
        </div>
      )}

      {/* Expense Detail Modal */}
      {selectedExpense && (
        <div className="expense-detail-modal">
          <div className="modal-backdrop" onClick={() => setSelectedExpense(null)} />
          <div className="modal-content">
            <ExpenseDetail 
              expense={selectedExpense}
              onClose={() => setSelectedExpense(null)}
              onUpdate={loadAdminData}
            />
          </div>
        </div>
      )}

      {/* Month Expenses Modal */}
      {selectedMonthExpenses && (
        <div className="month-expenses-modal">
          <div className="modal-backdrop" onClick={closeMonthExpensesModal} />
          <div className="modal-content large">
            <div className="modal-header">
              <h2>📅 Expenses for {selectedMonthLabel}</h2>
              <button className="close-button" onClick={closeMonthExpensesModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="month-summary">
                <span><strong>{selectedMonthExpenses.length}</strong> expenses</span>
                <span><strong>{formatAmount(selectedMonthExpenses.reduce((sum, e) => sum + e.amount, 0))}</strong> total</span>
              </div>
              {selectedMonthExpenses.length > 0 ? (
                <div className="expense-list-modal">
                  {selectedMonthExpenses.map(expense => {
                    const userInfo = getUserInfo(expense.submitterId);
                    return (
                      <div 
                        key={expense.id} 
                        className="expense-list-item"
                        onClick={() => { closeMonthExpensesModal(); setSelectedExpense(expense); }}
                      >
                        <div className="expense-list-info">
                          <h4>{expense.title}</h4>
                          <div className="expense-list-meta">
                            <span className="amount">{formatAmount(expense.amount)}</span>
                            <span className="category">{expense.category}</span>
                            <span className="date">{new Date(expense.date).toLocaleDateString()}</span>
                            <span className={`status ${getStatusColor(expense.status)}`}>{expense.status}</span>
                          </div>
                          <div className="submitter-info">
                            <span>👤 {userInfo.name}</span>
                            {userInfo.managerName && <span>👔 Manager: {userInfo.managerName}</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="empty-state">
                  <p>No expenses for this month.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
